package meineglasfaser.frontend.admin.components.widgets

import androidx.compose.runtime.*
import io.ktor.client.call.*
import io.ktor.client.statement.*
import io.ktor.util.reflect.*
import meineglasfaser.api.*
import meineglasfaser.api.ubl.UBLAmount
import meineglasfaser.api.ubl.UBLPostalAddress
import meineglasfaser.frontend.common.components.widgets.BootstrapIcon
import meineglasfaser.frontend.common.components.widgets.UBLPostalAddressView
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.disabled
import org.jetbrains.compose.web.attributes.placeholder
import org.jetbrains.compose.web.attributes.selected
import org.jetbrains.compose.web.dom.*
import kotlin.reflect.KProperty1
import kotlin.time.Instant

typealias YaTableProp<T> = KProperty1<T, *>

private fun makeSnakeCase(name: String): String {
    return name.replace(Regex("([a-z])([A-Z])"), "$1_$2").lowercase()
}

data class YaTableColumn<T>(
    val property: YaTableProp<T>,
    val dbColName: String,
    val displayName: String,
) {
    fun get(receiver: T) = property.get(receiver)
}

infix fun <T> YaTableProp<T>.named(displayName: String): YaTableColumn<T> {
    return YaTableColumn(
        property = this,
        dbColName = makeSnakeCase(this.name),
        displayName = displayName,
    )
}

fun <T> YaTableProp<T>.sortedAsc(): YaTableOptions.SortPair {
    return YaTableOptions.SortPair(makeSnakeCase(this.name), YaTableOptions.SortOrder.ASC)
}

fun <T> YaTableProp<T>.sortedDesc(): YaTableOptions.SortPair {
    return YaTableOptions.SortPair(makeSnakeCase(this.name), YaTableOptions.SortOrder.DESC)
}

fun <T> YaTableColumn<T>.sortedAsc(): YaTableOptions.SortPair {
    return YaTableOptions.SortPair(this.dbColName, YaTableOptions.SortOrder.ASC)
}

fun <T> YaTableColumn<T>.sortedDesc(): YaTableOptions.SortPair {
    return YaTableOptions.SortPair(this.dbColName, YaTableOptions.SortOrder.DESC)
}

fun <T> YaTableProp<T>.filterOnly(vararg elements: String): YaTableOptions.FilterPair {
    return YaTableOptions.FilterPair(makeSnakeCase(this.name), onlyValues = elements.toList())
}

@Composable
inline fun <reified T> YaTable(
    noinline header: @Composable () -> Unit,
    noinline subheader: @Composable (() -> Unit)? = null,
    idProperty: YaTableProp<T>,
    columns: List<YaTableColumn<T>>,
    initialOptions: YaTableOptions,
    noinline fetch: suspend (YaTableOptions) -> Result<HttpResponse>,
    noinline onNewEntry: (() -> Unit)? = null,
    noinline onViewEntry: ((T) -> Unit)? = null,
    noinline mapValue: ((column: String, Any?) -> Any?) = { _, v -> v },
    version: Int = 0,
    typeInfo: TypeInfo = typeInfo<YaTableResult<T>>(),
) {
    YaTableImpl(header, subheader, idProperty, columns, initialOptions, fetch, onNewEntry, onViewEntry, mapValue, version, typeInfo)
}

@Composable
fun <T> YaTableImpl(
    header: @Composable () -> Unit,
    subheader: @Composable (() -> Unit)? = null,
    idProperty: YaTableProp<T>,
    columns: List<YaTableColumn<T>>,
    initialOptions: YaTableOptions,
    fetch: suspend (YaTableOptions) -> Result<HttpResponse>,
    onNewEntry: (() -> Unit)?,
    onViewEntry: ((T) -> Unit)?,
    mapValue: (column: String, Any?) -> Any?,
    version: Int,
    typeInfo: TypeInfo,
) {
    var options by remember { mutableStateOf(initialOptions) }
    var result by remember { mutableStateOf<YaTableResult<T>?>(null) }

    LaunchedEffect(columns, options, fetch, version) {
        result = fetch(options).fold(
            onSuccess = { response ->
                response.body(typeInfo)
            },
            onFailure = { error ->
                console.error("Error fetching table data: ${error.message}")
                null
            }
        )
    }

    Div(attrs = { classes("paginated-table" )}) {
        Div({ classes("header") }) {
            header()
            Div({ classes("flex-row") }) {
                if (onNewEntry != null) {
                    Button({
                        onClick { onNewEntry() }
                    }) {
                        BootstrapIcon("plus")
                        Text("Neu")
                    }
                }
                Input(InputType.Search) {
                    placeholder("Suchen...")
                    onInput { options = options.copy(searchTerm = it.value) }
                }
            }
        }
        if (subheader != null) {
            Div({ classes("wrapper-subheader") }) {
                subheader()
            }
        }
        Div({ classes("wrapper") }) {
            Table({ classes("striped") }) {
                YoTableHead(columns, options, onOptionsChange = {
                    options = it
                })
                result?.let { result ->
                    YoTableBody(columns, result, onViewEntry, mapValue)
                }
            }
        }
        YoTableControls(result?.total ?: 0, options, onOptionsChange = {
            options = it
        })
    }

}

@Composable
private fun <T> YoTableHead(
    columns: List<YaTableColumn<T>>,
    options: YaTableOptions,
    onOptionsChange: (YaTableOptions) -> Unit,
) {
    Thead {
        Tr {
            columns.forEach { column ->
                val sortOrder = options.orderBy().find { it.colName == column.dbColName }?.dir
                Th {
                    Div {
                        Text(column.displayName)
                    }
                    Div({ classes("column-options") }) {
                        Button({
                            classes(if (sortOrder == null) "ghost" else "primary")
                            onClick { event ->
                                when (sortOrder) {
                                    null -> {
                                        onOptionsChange(options.copy(
                                            orderBy = listOf(column.sortedAsc())
                                        ))
                                    }
                                    YaTableOptions.SortOrder.ASC -> {
                                        onOptionsChange(options.copy(
                                            orderBy = listOf(column.sortedDesc())
                                        ))
                                    }
                                    YaTableOptions.SortOrder.DESC -> {
                                        onOptionsChange(options.copy(
                                            orderBy = listOf(column.sortedAsc())
                                        ))
                                    }
                                }
                            }
                        }) {
                            BootstrapIcon(when (sortOrder) {
                                null -> "arrow-down-up"
                                YaTableOptions.SortOrder.ASC -> "sort-alpha-down"
                                YaTableOptions.SortOrder.DESC -> "sort-alpha-up"
                            })
                        }
//                        Button({ classes("ghost") }) {
//                            BootstrapIcon("funnel")
//                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun <T> YoTableBody(
    columns: List<YaTableColumn<T>>,
    result: YaTableResult<T>,
    onViewEntry: ((T) -> Unit)?,
    mapValue: (column: String, Any?) -> Any?,
) {
    Tbody {
        result.data.forEach { row ->
            Tr({
                if (onViewEntry != null) {
                    classes("clickable")
                    onDoubleClick { onViewEntry(row) }
                }
            }) {
                columns.forEach { column ->
                    val cell = mapValue(column.property.name, column.get(row))
                    Td {
                        YoTableCell(cell)
                    }
                }
            }
        }
    }
}

@Composable
private fun YoTableCell(value: Any?) {
    when (value) {
        null -> Text("-")
        is Boolean -> BootstrapIcon(if (value) "check" else "x")
        is Instant -> Text(value.formatted())
        is UBLAmount -> Text(value.displayStrCurrency())
        is UBLPostalAddress -> UBLPostalAddressView(value, vertical = true)
        is SurveyInstallationType -> Text(value.title)
        is SurveyInstallationOption -> Text(value.title)
        is List<*> -> {
            if (value.isEmpty()) {
                Text("-")
            } else {
                value.forEachIndexed { index, item ->
                    if (index > 0) Br()
                    YoTableCell(item)
                }
            }
        }
        else -> Text(value.toString())
    }
}

@Composable
private fun YoTableControls(
    total: Int,
    options: YaTableOptions,
    onOptionsChange: (YaTableOptions) -> Unit
) {
    val currentPage = options.page()
    val perPage = options.perPage()
    val totalPages = (total + perPage - 1) / perPage
    val pages = remember(totalPages, currentPage) {
        when {
            totalPages == 0 -> listOf(1)
            totalPages <= 7 -> (1..totalPages).toList()
            totalPages > 7 && currentPage <= 4 -> buildList {
                addAll(1..5)
                add(-1)
                add(totalPages)
            }
            totalPages > 7 && currentPage >= (totalPages - 3) -> buildList {
                add(1)
                add(-1)
                addAll((totalPages - 4)..totalPages)
            }
            else -> buildList {
                add(1)
                add(-1)
                add(currentPage - 1)
                add(currentPage)
                add(currentPage + 1)
                add(-1)
                add(totalPages)
            }
        }
    }

    val startEntry = ((currentPage - 1) * perPage + 1).coerceAtMost(total)
    val endEntry = (currentPage * perPage).coerceAtMost(total)

    Div({ classes("controls") }) {
        Div({ classes("entries-info") }) {
            Text("$startEntry–$endEntry von $total")
        }

        Nav({ classes("page-control") }) {
            Ul {
                Li {
                    Button({
                        if (currentPage == 1) disabled()
                        onClick { if (currentPage > 1) onOptionsChange(options.copy(page = currentPage - 1)) }
                    }) {
                        Text("«")
                    }
                }

                for (page in pages) {
                    Li {
                        Button({
                            classes("page-btn")
                            if (page != currentPage) classes("ghost")
                            if (page == currentPage || page < 0) disabled()
                            onClick { onOptionsChange(options.copy(page = page)) }
                        }) {
                            Text(if (page < 0) "…" else page.toString())
                        }
                    }
                }

                Li {
                    Button({
                        if (currentPage >= totalPages) disabled()
                        onClick { if (currentPage < totalPages) onOptionsChange(options.copy(page = currentPage + 1)) }
                    }) {
                        Text("»")
                    }
                }
            }
        }

        Div({ classes("per-page-control") }) {
            Select(attrs = {
                onChange {
                    val newPerPage = it.value?.toIntOrNull() ?: perPage
                    onOptionsChange(options.copy(page = 1, perPage = newPerPage))
                }
            }) {
                listOf(10, 25, 50, 100).forEach { value ->
                    Option(value.toString(), {
                        if (value == options.perPage()) {
                            selected()
                        }
                    }) {
                        Text(value.toString())
                    }
                }
            }
        }
    }
}
